import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import { useLocation } from 'react-router-dom';

import {
  setSelectedForm,
  logIn,
} from '../actions';
import {
  FORMS__REMIND_PASSWORD,
  FORMS__SIGN_UP,
  FORMS__SIGN_IN_WITH_PASSWORD_CHANGE,
} from '../constants';

import {
  setState,
} from '../../actions';
import {
  STATE__ANONYMOUS,
  STATE__AUTHORIZED,
} from '../../constants';

import {
  setSelectedMenuItem,
  setExpirationDate,
} from '../../authorizedState/actions';
import {
  MENU_ITEM__QUESTIONNAIRES__AVAILABLE,
  MENU_ITEM__FORMS__ASSIGNED,
} from '../../authorizedState/constants';

import { getValidationError, setValidationError } from '../../helpers/dataOperations/form/validationMessageHelper';

import initialEditorState from './initialEditorState';
import validateEditorState from './validateEditorState';
import FormNotification from '../../shared/components/formNotification';
import notificationTypes from '../../shared/components/formNotification/notificationTypes';
import ActionButton from '../../shared/components/actionButton';

const SignIn = ({ onEmailChange, selectedFormParams }) => {
  const dispatch = useDispatch();
  const formMessage = useSelector((store) => store.unauthorizedState.get('formMessage'));
  const [validationMessage, setValidationMessage] = useState();
  const [editorState, setEditorState] = useState(initialEditorState());
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();

  const singInSuccess = (data) => {
    setIsLoading(false);
    dispatch(setState(STATE__AUTHORIZED));
    dispatch(setExpirationDate(data.refreshTokenExpireDate));
    dispatch(setFormMessage(''));
    dispatch(setSelectedMenuItem(MENU_ITEM__FORMS__ASSIGNED));
  };

  const signInFailure = (exception) => {
    setIsLoading(false);
    if (exception.response && exception.response.status === 401) {
      setValidationMessage(setValidationError(exception.response.data.message, 'form'));
    }

    if (exception.response && exception.response.status === 409) {
      dispatch(setSelectedForm(FORMS__SIGN_IN_WITH_PASSWORD_CHANGE));
    }
  };

  const signInButtonClick = () => {
    const validateMessage = validateEditorState(editorState);
    if (validateMessage) {
      setValidationMessage(validateMessage);
    } else {
      setIsLoading(true);
      dispatch(logIn(editorState, singInSuccess, signInFailure));
    }
  };

  const signInAnonymouslyButtonClick = () => {
    dispatch(setState(STATE__ANONYMOUS));
    dispatch(setSelectedMenuItem(MENU_ITEM__QUESTIONNAIRES__AVAILABLE));
  };

  const signUpButtonClick = () => {
    dispatch(setSelectedForm(FORMS__SIGN_UP));
  };

  const remindPasswordButtonClick = () => {
    dispatch(setSelectedForm(FORMS__REMIND_PASSWORD));
  };

  const onChange = (name, value) => {
    if (name === 'email') {
      onEmailChange(value);
    }
    setEditorState({ ...editorState, [name]: value });
  };

  const onFormClick = (event) => {
    if (event.key === 'Enter') {
      signInButtonClick();
    }
  };

  return (
    <div className="sign-in">
      <div className="title">
        Logowanie
      </div>
      <div className="description">
        Zaloguj się na swoje konto lub skorzystaj z portalu anonimowo.
      </div>
      <div className="form" role="button" onKeyDown={onFormClick}>
        <div className="form-group">
          <div className="error-message">
            {getValidationError(validationMessage, 'form')}
            {getValidationError(validationMessage, 'email')}
          </div>
          <label htmlFor="email">
            <div className="required-label-field">
              E-mail
            </div>
            <input
              className="form-control"
              id="email"
              type="email"
              value={editorState.email}
              onChange={(event) => onChange('email', event.target.value)}
            />
          </label>
        </div>
        <div className="form-group">
          <div className="error-message">
            {getValidationError(validationMessage, 'password')}
          </div>
          <label htmlFor="password">
            <div className="required-label-field">
              Hasło
            </div>
            <input
              className="form-control"
              id="password"
              type="password"
              value={editorState.password}
              onChange={(event) => onChange('password', event.target.value)}
            />
          </label>
        </div>
        <div className="remind-password-wrapper">
          <span
            className="remind-password-button"
            onClick={remindPasswordButtonClick}
            role="button"
            title="Nie pamiętasz hasła?"
          >
            Nie pamiętasz hasła?
          </span>
        </div>
      </div>
      <Choose>
        <When condition={selectedFormParams.registerSuccessAccountName}>
          <FormNotification
            message={{ value: 'Konto zostało utworzone i oczekuje na zatwierdzenie administratora.', type: notificationTypes.SUCCESS }}
          />
        </When>
        <When condition={formMessage}>
          <FormNotification
            message={{ value: formMessage, type: notificationTypes.SUCCESS }}
          />
        </When>
        <When condition={location?.state?.changePassword}>
          <FormNotification
            message={{ value: 'Hasło zostało zmienione.', type: notificationTypes.SUCCESS }}
          />
        </When>
      </Choose>
      <div className="actions">
        <ActionButton
          className="full-width"
          title="Zaloguj się"
          onClick={signInButtonClick}
          isLoading={isLoading}
        />
        <div className="sign-up-wrapper">
          <span>Nie masz konta? </span>
          <span
            className="sign-up-button"
            onClick={signUpButtonClick}
            role="button"
            title="Zarejestruj się"
          >
            Zarejestruj się!
          </span>
        </div>
      </div>
      <div className="separator-wrapper">
        <div className="separator">
          <span className="title">
            lub
          </span>
        </div>
      </div>
      <div className="actions">
        <div
          className="button sign-in-anonymously-button"
          onClick={signInAnonymouslyButtonClick}
          role="button"
          title="Przejdź do ankiet"
        >
          Przejdź do ankiet
        </div>
      </div>
      <If condition={!!window.env.SERVICE_TERMS_URL}>
        <a
          className="service-terms-url"
          href={window.env.SERVICE_TERMS_URL}
          rel="noreferrer"
          target="_blank"
        >
          Regulamin
        </a>
      </If>
      <div className="version-label">
        {`Wersja ${APPLICATION_VERSION}`}
      </div>
      <div className="funding-info" />
    </div>
  );
};

SignIn.propTypes = {
  onEmailChange: PropTypes.func.isRequired,
  selectedFormParams: PropTypes.object.isRequired, // eslint-disable-line
};

export default SignIn;
